import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  TextField,
  InputAdornment,
  Checkbox,
  Menu,
  // MenuList,
  MenuItem as MenuItemComponent,
  // ListItemText,
  // Divider
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Search as SearchIcon,
  FileDownload as FileDownloadIcon,
  PictureAsPdf as PictureAsPdfIcon,
  Description as DescriptionIcon
} from '@mui/icons-material';
import { getMonthlyAttendanceReport } from '../../Services/AttendanceManageServices';

const MonthlyAttendanceReport = () => {
  const isDarkTheme = useSelector((state) => state.theme.isDarkTheme);
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [exportMenuAnchor, setExportMenuAnchor] = useState(null);

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const years = Array.from({ length: 11 }, (_, i) => 2020 + i);
  console.log('Years array:', years); // Debug log

  const fetchMonthlyReport = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      console.log(`Fetching report for month: ${selectedMonth}, year: ${selectedYear}`);
      const response = await getMonthlyAttendanceReport(selectedMonth, selectedYear);
      console.log('API Response:', response.data);
      setReportData(response.data);
    } catch (err) {
      console.error('API Error:', err);
      setError(err.response?.data?.message || 'Failed to fetch report');
    } finally {
      setLoading(false);
    }
  }, [selectedMonth, selectedYear]);

  /**
   * Check if a date is Saturday
   * @param {string} dateString - Date in DD-MM-YYYY format
   * @returns {boolean} - True if Saturday
   */
  const isSaturday = (dateString) => {
    const [day, month, year] = dateString.split('-');
    const date = new Date(`${year}-${month}-${day}`);
    return date.getDay() === 6; // 6 = Saturday
  };

  // Calculate filtered reports
  const filteredReports = reportData ? reportData.reports.filter(employee =>
    employee.employeeName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    employee.empId.toLowerCase().includes(searchTerm.toLowerCase())
  ) : [];

  // Export functions
  const handleExportMenuOpen = (event) => {
    setExportMenuAnchor(event.currentTarget);
  };

  const handleExportMenuClose = () => {
    setExportMenuAnchor(null);
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      const allEmployeeIds = filteredReports.map(emp => emp.empId);
      setSelectedEmployees(allEmployeeIds);
    } else {
      setSelectedEmployees([]);
    }
  };

  const handleEmployeeSelection = (empId, checked) => {
    if (checked) {
      setSelectedEmployees([...selectedEmployees, empId]);
    } else {
      setSelectedEmployees(selectedEmployees.filter(id => id !== empId));
    }
  };

  const getSelectedEmployeesData = () => {
    return filteredReports.filter(emp => selectedEmployees.includes(emp.empId));
  };

  const exportToCSV = () => {
    const employees = getSelectedEmployeesData();
    if (employees.length === 0) return;

    let csvContent = 'Monthly Attendance Report - ' + months[selectedMonth] + ' ' + selectedYear + '\n\n';
    
    employees.forEach((emp, index) => {
      // Employee summary
      csvContent += `Employee ${index + 1}: ${emp.employeeName} (${emp.empId})\n`;
      csvContent += `Present Days: ${emp.workingDays}, Status: ${(emp.lateThresholdExceeded || emp.shortHoursThresholdExceeded) ? 'Violation' : 'Compliant'}\n\n`;
      
      // Late arrivals details
      if (emp.latePunchIns.length > 0) {
        csvContent += 'Late Arrivals:\n';
        csvContent += 'Date,Punch In Time,Total Working Hours\n';
        emp.latePunchIns.forEach(late => {
          csvContent += `${late.date},${late.punchInTime},${late.totalWorkingHours}\n`;
        });
        csvContent += '\n';
      }
      
      // Short working days details
      if (emp.shortWorkingDays.length > 0) {
        csvContent += 'Short Working Days:\n';
        csvContent += 'Date,Punch In Time,Punch Out Time,Total Working Hours,Is Saturday\n';
        emp.shortWorkingDays.forEach(short => {
          const saturdayFlag = isSaturday(short.date) ? 'Yes' : 'No';
          csvContent += `${short.date},${short.punchInTime},${short.punchOutTime},${short.totalWorkingHours},${saturdayFlag}\n`;
        });
        csvContent += '\n';
      }
      
      if (emp.latePunchIns.length === 0 && emp.shortWorkingDays.length === 0) {
        csvContent += 'No violations this month\n\n';
      }
      
      csvContent += '---\n\n';
    });

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `attendance_report_detailed_${selectedMonth + 1}_${selectedYear}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    handleExportMenuClose();
  };

  const exportToExcel = () => {
    // For Excel export, we'll create a simple CSV that Excel can open
    // In a real implementation, you might want to use a library like xlsx
    exportToCSV();
  };

  const exportToPDF = () => {
    const employees = getSelectedEmployeesData();
    if (employees.length === 0) return;

    const employeePages = employees.map((emp, index) => {
      let pageContent = `
        <div style="page-break-after: always; padding: 20px;">
          <h2>Employee ${index + 1}: ${emp.employeeName} (${emp.empId})</h2>
          <p><strong>Month:</strong> ${months[selectedMonth]} ${selectedYear}</p>
          <p><strong>Present Days:</strong> ${emp.workingDays}</p>
          <p><strong>Status:</strong> ${(emp.lateThresholdExceeded || emp.shortHoursThresholdExceeded) ? 'Violation' : 'Compliant'}</p>
          <p><strong>Late Count:</strong> ${emp.totalLateCount}/${emp.maxLateThreshold}</p>
          <p><strong>Short Hours Count:</strong> ${emp.totalShortHoursCount}/${emp.maxShortHoursThreshold}</p>
      `;

      if (emp.latePunchIns.length > 0) {
        pageContent += `
          <h3>Late Arrivals (${emp.latePunchIns.length})</h3>
          <table style="border-collapse: collapse; width: 100%; margin-bottom: 20px;">
            <thead>
              <tr style="background-color: #f2f2f2;">
                <th style="border: 1px solid #ddd; padding: 8px;">Date</th>
                <th style="border: 1px solid #ddd; padding: 8px;">Punch In Time</th>
                <th style="border: 1px solid #ddd; padding: 8px;">Total Working Hours</th>
              </tr>
            </thead>
            <tbody>
              ${emp.latePunchIns.map(late => `
                <tr>
                  <td style="border: 1px solid #ddd; padding: 8px;">${late.date}</td>
                  <td style="border: 1px solid #ddd; padding: 8px;">${late.punchInTime}</td>
                  <td style="border: 1px solid #ddd; padding: 8px;">${late.totalWorkingHours}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        `;
      }

      if (emp.shortWorkingDays.length > 0) {
        pageContent += `
          <h3>Short Working Days (${emp.shortWorkingDays.length})</h3>
          <table style="border-collapse: collapse; width: 100%; margin-bottom: 20px;">
            <thead>
              <tr style="background-color: #f2f2f2;">
                <th style="border: 1px solid #ddd; padding: 8px;">Date</th>
                <th style="border: 1px solid #ddd; padding: 8px;">Punch In Time</th>
                <th style="border: 1px solid #ddd; padding: 8px;">Punch Out Time</th>
                <th style="border: 1px solid #ddd; padding: 8px;">Total Working Hours</th>
              </tr>
            </thead>
            <tbody>
              ${emp.shortWorkingDays.map(short => {
                const isSaturdayDate = isSaturday(short.date);
                return `
                  <tr>
                    <td style="border: 1px solid #ddd; padding: 8px;">
                      ${short.date}
                      ${isSaturdayDate ? '<br><span style="color: #856404; font-size: 10px; font-style: italic;">Saturday</span>' : ''}
                    </td>
                    <td style="border: 1px solid #ddd; padding: 8px;">${short.punchInTime}</td>
                    <td style="border: 1px solid #ddd; padding: 8px;">${short.punchOutTime}</td>
                    <td style="border: 1px solid #ddd; padding: 8px;">${short.totalWorkingHours}</td>
                  </tr>
                `;
              }).join('')}
            </tbody>
          </table>
        `;
      }

      if (emp.latePunchIns.length === 0 && emp.shortWorkingDays.length === 0) {
        pageContent += '<p style="color: green; font-weight: bold;">No violations this month</p>';
      }

      pageContent += '</div>';
      return pageContent;
    }).join('');

    const html = `
      <html>
        <head>
          <title>Monthly Attendance Report - ${months[selectedMonth]} ${selectedYear}</title>
          <style>
            body { 
              font-family: Arial, sans-serif; 
              margin: 0; 
              padding: 20px;
              font-size: 12px;
            }
            h1, h2, h3 { color: #333; }
            h1 { text-align: center; margin-bottom: 30px; }
            h2 { border-bottom: 2px solid #007bff; padding-bottom: 5px; }
            table { border-collapse: collapse; width: 100%; margin-bottom: 20px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; font-weight: bold; }
            .page-break { page-break-after: always; }
            @media print {
              .page-break { page-break-after: always; }
            }
          </style>
        </head>
        <body>
          <h1>Monthly Attendance Report - ${months[selectedMonth]} ${selectedYear}</h1>
          ${employeePages}
        </body>
      </html>
    `;

    const printWindow = window.open('', '_blank', 'width=800,height=600');
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.print();
    handleExportMenuClose();
  };

  useEffect(() => {
    fetchMonthlyReport();
  }, [fetchMonthlyReport]);

  const getStatusColor = (exceeded) => (exceeded ? 'error' : 'success');
  const getStatusIcon = (exceeded) => (exceeded ? <WarningIcon /> : <CheckCircleIcon />);

  if (loading && !reportData) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box 
      p={3}
      sx={{
        backgroundColor: isDarkTheme ? 'var(--website-background-color)' : 'transparent',
        color: isDarkTheme ? 'var(--primary-text-color)' : 'inherit'
      }}
    >
      <Typography variant="h4" gutterBottom>
        Monthly Attendance Report
      </Typography>

      {/* Month/Year Selector and Search */}
      <Box mb={3} display="flex" gap={2} alignItems="center" flexWrap="wrap">
        <FormControl variant="outlined" size="small" style={{ minWidth: 120 }}>
          <InputLabel 
            sx={{
              color: isDarkTheme ? 'var(--secondary-text-color)' : 'inherit'
            }}
          >
            Month
          </InputLabel>
          <Select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            label="Month"
            sx={{
              color: isDarkTheme ? 'var(--secondary-text-color)' : 'inherit',
              '.MuiOutlinedInput-notchedOutline': {
                borderColor: isDarkTheme ? 'var(--secondary-text-color)' : 'inherit'
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: isDarkTheme ? 'var(--primary-text-color)' : 'inherit'
              },
              '.MuiSvgIcon-root': {
                color: isDarkTheme ? 'var(--secondary-text-color)' : 'inherit'
              }
            }}
          >
            {months.map((month, index) => (
              <MenuItem key={month} value={index}>
                {month}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl variant="outlined" size="small" style={{ minWidth: 100 }}>
          <InputLabel 
            sx={{
              color: isDarkTheme ? 'var(--secondary-text-color)' : 'inherit'
            }}
          >
            Year
          </InputLabel>
          <Select
            value={selectedYear}
            onChange={(e) => setSelectedYear(e.target.value)}
            label="Year"
            sx={{
              color: isDarkTheme ? 'var(--secondary-text-color)' : 'inherit',
              '.MuiOutlinedInput-notchedOutline': {
                borderColor: isDarkTheme ? 'var(--secondary-text-color)' : 'inherit'
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: isDarkTheme ? 'var(--primary-text-color)' : 'inherit'
              },
              '.MuiSvgIcon-root': {
                color: isDarkTheme ? 'var(--secondary-text-color)' : 'inherit'
              }
            }}
          >
            {years.map((year) => (
              <MenuItem key={year} value={year}>
                {year}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          variant="outlined"
          size="small"
          placeholder="Search by employee name..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: isDarkTheme ? 'var(--secondary-text-color)' : 'inherit' }} />
              </InputAdornment>
            ),
          }}
          sx={{
            minWidth: 200,
            '& .MuiOutlinedInput-input': {
              color: isDarkTheme ? 'var(--secondary-text-color)' : 'inherit'
            },
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: isDarkTheme ? 'var(--secondary-text-color)' : 'inherit'
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: isDarkTheme ? 'var(--primary-text-color)' : 'inherit'
            },
            '& .MuiInputLabel-root': {
              color: isDarkTheme ? 'var(--secondary-text-color)' : 'inherit'
            }
          }}
        />

        {/* <Button 
          variant="contained" 
          onClick={fetchMonthlyReport} 
          disabled={loading}
          sx={{
            backgroundColor: isDarkTheme ? 'var(--primary-color)' : 'inherit',
            color: isDarkTheme ? '#fff' : 'inherit',
            '&:hover': {
              backgroundColor: isDarkTheme ? 'var(--primary-color)' : 'inherit'
            }
          }}
        >
          {loading ? <CircularProgress size={20} /> : 'Generate Report'}
        </Button> */}

        <Button
          variant="outlined"
          startIcon={<FileDownloadIcon />}
          onClick={handleExportMenuOpen}
          disabled={selectedEmployees.length === 0}
          sx={{
            borderColor: isDarkTheme ? 'var(--secondary-text-color)' : 'inherit',
            color: isDarkTheme ? 'var(--secondary-text-color)' : 'inherit',
            '&:hover': {
              borderColor: isDarkTheme ? 'var(--primary-text-color)' : 'inherit'
            }
          }}
        >
          Export ({selectedEmployees.length})
        </Button>

        <Menu
          anchorEl={exportMenuAnchor}
          open={Boolean(exportMenuAnchor)}
          onClose={handleExportMenuClose}
          sx={{
            zIndex: 1300,
            '& .MuiList-root': {
              backgroundColor: isDarkTheme ? 'var(--background-table-sidebar-card-color)' : 'inherit',
              color: isDarkTheme ? 'var(--primary-text-color)' : 'inherit',
              zIndex: 1300
            }
          }}
        >
          <MenuItemComponent onClick={exportToCSV}>
            <DescriptionIcon sx={{ mr: 1 }} />
            Export to CSV
          </MenuItemComponent>
          <MenuItemComponent onClick={exportToExcel}>
            <DescriptionIcon sx={{ mr: 1 }} />
            Export to Excel
          </MenuItemComponent>
          <MenuItemComponent onClick={exportToPDF}>
            <PictureAsPdfIcon sx={{ mr: 1 }} />
            Export to PDF
          </MenuItemComponent>
        </Menu>
      </Box>

      {error && (
        <Alert 
          severity="error" 
          style={{ 
            marginBottom: 20,
            backgroundColor: isDarkTheme ? 'var(--reject-highlight-color)' : 'inherit',
            color: isDarkTheme ? 'var(--primary-text-color)' : 'inherit'
          }}
        >
          {error}
        </Alert>
      )}

      {reportData && (
        <>
          {/* Summary Cards */}
          <Grid container spacing={3} mb={3}>
            <Grid item xs={12} md={3}>
              <Card sx={{
                backgroundColor: isDarkTheme ? 'var(--background-table-sidebar-card-color)' : 'inherit',
                color: isDarkTheme ? 'var(--primary-text-color)' : 'inherit'
              }}>
                <CardContent>
                  <Typography variant="h6" color="primary">
                    Total Employees
                  </Typography>
                  <Typography variant="h4">
                    {filteredReports.length}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card sx={{
                backgroundColor: isDarkTheme ? 'var(--background-table-sidebar-card-color)' : 'inherit',
                color: isDarkTheme ? 'var(--primary-text-color)' : 'inherit'
              }}>
                <CardContent>
                  <Typography variant="h6" color="warning.main">
                    Late Arrivals
                  </Typography>
                  <Typography variant="h4">
                    {filteredReports.reduce((sum, emp) => sum + emp.totalLateCount, 0)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card sx={{
                backgroundColor: isDarkTheme ? 'var(--background-table-sidebar-card-color)' : 'inherit',
                color: isDarkTheme ? 'var(--primary-text-color)' : 'inherit'
              }}>
                <CardContent>
                  <Typography variant="h6" color="error.main">
                    Short Hours
                  </Typography>
                  <Typography variant="h4">
                    {filteredReports.reduce((sum, emp) => sum + emp.totalShortHoursCount, 0)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card sx={{
                backgroundColor: isDarkTheme ? 'var(--background-table-sidebar-card-color)' : 'inherit',
                color: isDarkTheme ? 'var(--primary-text-color)' : 'inherit'
              }}>
                <CardContent>
                  <Typography variant="h6" color="success.main">
                    Compliant
                  </Typography>
                  <Typography variant="h4">
                    {filteredReports.filter(emp => 
                      !emp.lateThresholdExceeded && !emp.shortHoursThresholdExceeded
                    ).length}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Employee Details Table */}
          <TableContainer 
            component={Paper}
            sx={{
              backgroundColor: isDarkTheme ? 'var(--background-table-sidebar-card-color)' : 'inherit',
              color: isDarkTheme ? 'var(--primary-text-color)' : 'inherit'
            }}
          >
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={selectedEmployees.length > 0 && selectedEmployees.length < filteredReports.length}
                      checked={filteredReports.length > 0 && selectedEmployees.length === filteredReports.length}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      sx={{
                        color: isDarkTheme ? 'var(--secondary-text-color)' : 'inherit'
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ color: isDarkTheme ? 'var(--primary-text-color)' : 'inherit' }}>Employee</TableCell>
                  <TableCell sx={{ color: isDarkTheme ? 'var(--primary-text-color)' : 'inherit' }}>Present Days</TableCell>
                  <TableCell sx={{ color: isDarkTheme ? 'var(--primary-text-color)' : 'inherit' }}>Late Arrivals</TableCell>
                  <TableCell sx={{ color: isDarkTheme ? 'var(--primary-text-color)' : 'inherit' }}>Short Hours</TableCell>
                  <TableCell sx={{ color: isDarkTheme ? 'var(--primary-text-color)' : 'inherit' }}>Status</TableCell>
                  <TableCell sx={{ color: isDarkTheme ? 'var(--primary-text-color)' : 'inherit' }}>Details</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredReports.map((employee) => (
                  <TableRow key={employee.empId}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedEmployees.includes(employee.empId)}
                        onChange={(e) => handleEmployeeSelection(employee.empId, e.target.checked)}
                        sx={{
                          color: isDarkTheme ? 'var(--secondary-text-color)' : 'inherit'
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ color: isDarkTheme ? 'var(--primary-text-color)' : 'inherit' }}>
                      <Typography variant="body2" fontWeight="bold">
                        {employee.employeeName}
                      </Typography>
                      <Typography variant="caption">
                        {employee.empId}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ color: isDarkTheme ? 'var(--primary-text-color)' : 'inherit' }}>{employee.workingDays}</TableCell>
                    <TableCell sx={{ color: isDarkTheme ? 'var(--primary-text-color)' : 'inherit' }}>
                      <Chip
                        label={`${employee.totalLateCount}/${employee.maxLateThreshold}`}
                        color={getStatusColor(employee.lateThresholdExceeded)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell sx={{ color: isDarkTheme ? 'var(--primary-text-color)' : 'inherit' }}>
                      <Chip
                        label={`${employee.totalShortHoursCount}/${employee.maxShortHoursThreshold}`}
                        color={getStatusColor(employee.shortHoursThresholdExceeded)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell sx={{ color: isDarkTheme ? 'var(--primary-text-color)' : 'inherit' }}>
                      <Chip
                        icon={getStatusIcon(employee.lateThresholdExceeded || employee.shortHoursThresholdExceeded)}
                        label={employee.lateThresholdExceeded || employee.shortHoursThresholdExceeded ? 'Violation' : 'Compliant'}
                        color={getStatusColor(employee.lateThresholdExceeded || employee.shortHoursThresholdExceeded)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell sx={{ color: isDarkTheme ? 'var(--primary-text-color)' : 'inherit' }}>
                      <Accordion sx={{
                        backgroundColor: isDarkTheme ? 'var(--background-table-sidebar-card-color)' : 'inherit',
                        color: isDarkTheme ? 'var(--primary-text-color)' : 'inherit'
                      }}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Typography variant="body2">View Details</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Box>
                            {/* Late Arrivals Details */}
                            {employee.latePunchIns.length > 0 && (
                              <Box mb={2}>
                                <Typography variant="subtitle2" color="warning.main" gutterBottom>
                                  Late Arrivals ({employee.latePunchIns.length})
                                </Typography>
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell sx={{ color: isDarkTheme ? 'var(--primary-text-color)' : 'inherit' }}>Date</TableCell>
                                      <TableCell sx={{ color: isDarkTheme ? 'var(--primary-text-color)' : 'inherit' }}>Punch In</TableCell>
                                      <TableCell sx={{ color: isDarkTheme ? 'var(--primary-text-color)' : 'inherit' }}>Total Hours</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {employee.latePunchIns.map((late, index) => (
                                      <TableRow key={index}>
                                        <TableCell sx={{ color: isDarkTheme ? 'var(--primary-text-color)' : 'inherit' }}>{late.date}</TableCell>
                                        <TableCell sx={{ color: isDarkTheme ? 'var(--primary-text-color)' : 'inherit' }}>{late.punchInTime}</TableCell>
                                        <TableCell sx={{ color: isDarkTheme ? 'var(--primary-text-color)' : 'inherit' }}>{late.totalWorkingHours}</TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </Box>
                            )}

                            {/* Short Working Hours Details */}
                            {employee.shortWorkingDays.length > 0 && (
                              <Box>
                                <Typography variant="subtitle2" color="error.main" gutterBottom>
                                  Short Working Days ({employee.shortWorkingDays.length})
                                </Typography>
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell sx={{ color: isDarkTheme ? 'var(--primary-text-color)' : 'inherit' }}>Date</TableCell>
                                      <TableCell sx={{ color: isDarkTheme ? 'var(--primary-text-color)' : 'inherit' }}>Punch In</TableCell>
                                      <TableCell sx={{ color: isDarkTheme ? 'var(--primary-text-color)' : 'inherit' }}>Punch Out</TableCell>
                                      <TableCell sx={{ color: isDarkTheme ? 'var(--primary-text-color)' : 'inherit' }}>Total Hours</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {employee.shortWorkingDays.map((short, index) => {
                                      const isSaturdayDate = isSaturday(short.date);
                                      return (
                                        <TableRow key={index}>
                                          <TableCell sx={{ color: isDarkTheme ? 'var(--primary-text-color)' : 'inherit' }}>
                                            <Box display="flex" alignItems="center" gap={1}>
                                              {short.date}
                                              {isSaturdayDate && (
                                                <Chip
                                                  label="S"
                                                  size="small"
                                                  sx={{
                                                    backgroundColor: 'warning.light',
                                                    color: 'warning.dark',
                                                    fontWeight: 'bold',
                                                    fontSize: '0.7rem',
                                                    height: '20px'
                                                  }}
                                                  title="Saturday"
                                                />
                                              )}
                                            </Box>
                                          </TableCell>
                                          <TableCell sx={{ color: isDarkTheme ? 'var(--primary-text-color)' : 'inherit' }}>{short.punchInTime}</TableCell>
                                          <TableCell sx={{ color: isDarkTheme ? 'var(--primary-text-color)' : 'inherit' }}>{short.punchOutTime}</TableCell>
                                          <TableCell sx={{ color: isDarkTheme ? 'var(--primary-text-color)' : 'inherit' }}>{short.totalWorkingHours}</TableCell>
                                        </TableRow>
                                      );
                                    })}
                                  </TableBody>
                                </Table>
                              </Box>
                            )}

                            {/* No Violations */}
                            {employee.latePunchIns.length === 0 && employee.shortWorkingDays.length === 0 && (
                              <Typography variant="body2" color="success.main">
                                No violations this month
                              </Typography>
                            )}
                          </Box>
                        </AccordionDetails>
                      </Accordion>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Box>
  );
};

export default MonthlyAttendanceReport;
